import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { CollapsedOperationDisplay } from "./CollapsedOperationDisplay";
import { WorkflowRunViewReasoning } from "./WorkflowRunViewReasoning";
import { GroupEntry } from "./WorkflowRunViewWithOperations";

interface GroupedCollapsedOperationsProps {
  groupEntries: GroupEntry[];
  isInProgress: boolean;
}

/**
 * Displays a grouped section of collapsed operations.
 * Shows "Agent Working" if in progress, "Agent Worked" if completed.
 * When expanded, shows all individual CollapsedOperationDisplay components.
 */
export const GroupedCollapsedOperations: React.FC<
  GroupedCollapsedOperationsProps
> = ({ groupEntries, isInProgress }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const validEntries = groupEntries.filter((entry) => {
    if (entry.type === "reasoning") {
      return entry.content && entry.content.trim().length > 0;
    }
    return true;
  });

  const sortedEntries = [...validEntries].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );
  const label = isInProgress ? "Agent Working" : "Agent Worked";
  const entryCount = validEntries.length;

  if (sortedEntries.length === 0) {
    return null;
  }

  return (
    <div className="pl-11">
      <div className="border rounded-lg overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-muted hover:bg-muted/80 transition-colors"
        >
          {isInProgress ? (
            <Loader2 className="h-3 w-3 text-blue-500 shrink-0 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
          )}
          <span className="flex-1 text-left font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">
            {entryCount} {entryCount === 1 ? "item" : "items"}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
        <div
          className={`${isExpanded ? "max-h-[800px]" : "max-h-0"} transition-all duration-200 ease-in-out overflow-hidden`}
        >
          <div className="p-3 space-y-2">
            {sortedEntries.map((entry) => {
              if (entry.type === "reasoning") {
                return (
                  <WorkflowRunViewReasoning
                    key={entry.id}
                    id={entry.id}
                    content={entry.content}
                    className="pl-0"
                  />
                );
              }

              if (entry.type === "operation") {
                return (
                  <CollapsedOperationDisplay
                    key={entry.id}
                    operation={entry.operation}
                  />
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
